import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Lock, Pencil, Plus } from 'lucide-react';
import {
  Accordion,
  Card,
  PageBody,
  ViewTab,
  ViewTabList,
  buttonVariants,
  iconButtonVariants,
} from '@wundo/ui';
import type { RoleplayCard, RoleplayListResponse } from '@wundo/shared/roleplay.ts';
import { PageTitle } from '@/components/PageTitle.tsx';
import {
  PracticeCard,
  type PracticeCardProps,
} from '@/components/practice/PracticeCard.tsx';
import { PracticeFeedbackDialog } from '@/components/practice/PracticeFeedbackDialog.tsx';
import { PracticeLibraryOrder } from '@/components/practice/PracticeLibraryOrder.tsx';
import { PracticeSessionModal } from '@/components/practice/PracticeSessionModal.tsx';
import { SortablePracticeCard } from '@/components/practice/SortablePracticeCard.tsx';
import { thisWeekFirst } from '@/components/practice/thisWeekFirst.ts';
import { reorderRoleplays, startRoleplaySession } from '@/lib/roleplays.ts';
import { PILOT_CONTEXT_KEY, fetchPilotContext } from '@wundo/client/pilot.ts';
import { fetchCurrentUser } from '@wundo/client/auth.ts';
import { usePublishChatAnchor } from '@/lib/chatAnchor.ts';
import { usePublishChatPageOffer } from '@/lib/chatPageOffer.ts';
import { roleplaysQuery } from '@/lib/roleplaysQuery.ts';
import { useDemoHoldBack } from '@/lib/useDemoHoldBack.ts';
import { m } from '@/paraglide/messages.js';

/** What the page can be asked to open with. */
interface PracticeSearch {
  /** Show this roleplay's feedback straight away. */
  feedback?: string;
  /** Hold this practice conversation open over the page. */
  session?: string;
}

export const Route = createFileRoute('/_app/practice/')({
  /*
   * A practice conversation ends by coming back here with the feedback named,
   * so the debrief is read where the earlier attempts are — one place that
   * compares them, rather than a copy of it on the way out of a session.
   *
   * A running one is named too, so a reload mid-conversation opens the page
   * with the conversation back over it rather than dropping the call.
   */
  validateSearch: (search: Record<string, unknown>): PracticeSearch => ({
    feedback: typeof search.feedback === 'string' ? search.feedback : undefined,
    session: typeof search.session === 'string' ? search.session : undefined,
  }),
  component: PracticeRoute,
});

function PracticeRoute() {
  const { feedback, session } = Route.useSearch();
  return <PracticePage feedbackRoleplayId={feedback} sessionId={session} />;
}

type PracticeTab = 'library' | 'personal';

/**
 * A list with nothing in it, in the shape the rest of the page uses: the same
 * bordered card as a roleplay, read left to right.
 *
 * Not the centred `EmptyState`. This one stands where a list of cards would
 * stand, and a short line centred over the width of a card row reads as a
 * card that failed to render rather than as an answer.
 */
function PracticeEmpty({ title, body }: { title: string; body: string }) {
  return (
    <Card as='section' className='max-w-165'>
      <h2 className='text-md font-bold text-ink'>{title}</h2>
      <p className='mt-1.5 text-base leading-relaxed text-ink-muted'>{body}</p>
    </Card>
  );
}

export interface PracticePageProps {
  /** Opens this roleplay's feedback on arrival. */
  feedbackRoleplayId?: string;
  /** Arrives with this roleplay's card unfolded. */
  openRoleplayId?: string;
  /** Arrives with this practice conversation already open over the page. */
  sessionId?: string;
  /** Starts a practice of this roleplay on arrival, as its start button would. */
  startRoleplayId?: string;
}

/**
 * Oefenen: the conversations you can practise before you have them.
 *
 * Two lists, one page. The library is what the organisation put there for
 * everybody; "Voor mijn gesprekken" is what was made for you alone, out of your
 * own chat. They are the same card and the same practice — the split is about
 * who a conversation is for, not about what happens when you start it.
 *
 * An admin sees no separate screen: the same page carries the drafts, marked as
 * such, and the way to add one. A second admin-only list of the same rows is
 * how the two versions drift apart.
 */
export function PracticePage({
  feedbackRoleplayId,
  openRoleplayId,
  sessionId,
  startRoleplayId,
}: PracticePageProps = {}) {
  /*
   * What the chat is sitting beside while this page is open. The page itself is
   * the whole anchor: everything on it is the reader's own, so there is no id to
   * send along.
   */
  usePublishChatAnchor({ type: 'practice' });

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  /* Null until a tab is clicked: until then the address decides which one. */
  const [pickedTab, setPickedTab] = useState<PracticeTab | null>(null);
  /* One card at a time; the accordion hands back the open one as a list. */
  const [openIds, setOpenIds] = useState<string[]>(
    openRoleplayId ? [openRoleplayId] : [],
  );
  /* Whose feedback is showing. Null means the dialog is closed. */
  const [feedbackId, setFeedbackId] = useState<string | null>(feedbackRoleplayId ?? null);
  /*
   * The practice conversation held over the page. Null means there is none.
   *
   * Read out of the address on arrival and kept here afterwards, the same way
   * the feedback is: the address follows what is on screen so a reload comes
   * back to the same conversation, but what is on screen is not at the mercy of
   * a stray Back — there is a live microphone behind this one.
   */
  const [liveSessionId, setLiveSessionId] = useState<string | null>(sessionId ?? null);

  const { data, isPending, isError } = useQuery(roleplaysQuery());

  /*
   * A personal practice conversation comes out of the chat, which a demo
   * organisation does not have, so the library is all there is to show.
   */
  const { data: user } = useQuery({
    queryKey: ['currentUser'],
    queryFn: fetchCurrentUser,
  });
  const demo = user?.demo === true;
  /* A demo practises the library as it is: writing or arranging conversations is held back. */
  const holdBack = useDemoHoldBack();

  /* Null for everybody but a pilot member, and then this is their week's card. */
  const { data: pilot } = useQuery({
    queryKey: PILOT_CONTEXT_KEY,
    queryFn: fetchPilotContext,
  });
  const thisWeekId = pilot?.practice?.id ?? null;

  /*
   * Never for an admin arranging the library: they move cards against the
   * order that is stored, and a pin sitting on top of it would be saved as
   * that order the first time they move a card.
   */
  const library = thisWeekFirst(
    data?.library ?? [],
    data?.can_manage === true ? null : thisWeekId,
  );
  const all = [...library, ...(data?.personal ?? [])];
  /* Counted per user and only over finished sessions, so this is practice done. */
  const practised = all.some((card) => card.attempt_count > 0);

  /*
   * This page's conversation starters (DEV-16, DEV-97). The last one is the
   * only pill here about the person rather than the page: asking what you
   * learned needs a practice conversation you actually finished, so somebody
   * with none — or a list still loading — is not offered it.
   */
  usePublishChatPageOffer({
    quickActions: [
      { text: m.chat_quick_practice_which_first(), icon: 'roleplay' },
      { text: m.chat_quick_practice_which_skill(), icon: 'tips' },
      { text: m.chat_quick_practice_own(), icon: 'prepare' },
      ...(practised
        ? [{ text: m.chat_quick_practice_last_learned(), icon: 'recap' as const }]
        : []),
    ],
  });

  /*
   * Starting hands back a session and the conversation opens over the page.
   * The practice needs the whole window — nothing about it fits beside a list —
   * but it lasts ten minutes and comes straight back here, and leaving the page
   * for it cost the list its tab, its scroll and whichever card was open.
   */
  const start = useMutation({
    mutationFn: (roleplayId: string) => startRoleplaySession(roleplayId),
    onSuccess: (session) => {
      setLiveSessionId(session.id);
      void navigate({ to: '/practice', search: { session: session.id }, replace: true });
    },
  });

  /*
   * Started on arrival, never when the link was sent: a card sent days ahead
   * would find its session gone. The address loses its `start` first, so a
   * reload or Back cannot start a second one; a start that fails leaves the
   * card unfolded with the reason, as the plain address would show it.
   */
  const startedOnArrival = useRef(false);
  useEffect(() => {
    if (!startRoleplayId || !data || startedOnArrival.current) return;
    startedOnArrival.current = true;

    void navigate({
      to: '/practice/$roleplayId',
      params: { roleplayId: startRoleplayId },
      search: {},
      replace: true,
    });
    if ([...data.library, ...data.personal].some((card) => card.id === startRoleplayId)) {
      start.mutate(startRoleplayId);
    }
  }, [startRoleplayId, data, navigate, start]);

  /*
   * Arranging the library. The list is written on the page before the request
   * is answered: an author moving two cards should not have to wait for the
   * first move to land, and the card has already moved under their cursor.
   *
   * The whole order is sent every time, so what is stored is exactly the list
   * that was on the screen. A refused save puts the old order back.
   */
  const reorder = useMutation({
    mutationFn: (ids: string[]) => reorderRoleplays(ids),
    onMutate: async (ids: string[]) => {
      await queryClient.cancelQueries({ queryKey: ['roleplays'] });
      const previous = queryClient.getQueryData<RoleplayListResponse>(['roleplays']);

      if (previous !== undefined) {
        queryClient.setQueryData<RoleplayListResponse>(['roleplays'], {
          ...previous,
          library: ids
            .map((id) => previous.library.find((card) => card.id === id))
            .filter((card) => card !== undefined),
        });
      }

      return { previous };
    },
    onError: (_error, _ids, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(['roleplays'], context.previous);
      }
    },
    /* Right or wrong, the server has the last word on the order. */
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['roleplays'] }),
  });

  /*
   * Somebody who just finished practising arrives with their roleplay named,
   * and the feedback is the reason they are here. It is read out of the
   * address rather than copied into state on arrival: the dialog is then open
   * from the first render, and closing it is final — a list that refetches
   * cannot put it back.
   */
  const feedbackFor: RoleplayCard | null =
    all.find((candidate) => candidate.id === feedbackId) ?? null;

  /* Arriving on your own practice conversation opens the tab it is on. */
  const arrived = all.find(
    (candidate) => candidate.id === feedbackRoleplayId || candidate.id === openRoleplayId,
  );
  const tab: PracticeTab = demo
    ? 'library'
    : (pickedTab ?? (arrived?.kind === 'personal' ? 'personal' : 'library'));

  const cards = tab === 'library' ? library : (data?.personal ?? []);
  const empty = !isPending && !isError && cards.length === 0;

  const sortable = data?.can_manage === true && tab === 'library' && !demo;

  /* What every card on the page gets, arranged or not. */
  function cardProps(card: RoleplayCard): Omit<PracticeCardProps, 'handle'> {
    return {
      card,
      starting: start.isPending,
      expanded: openIds.includes(card.id),
      canManage: data?.can_manage === true,
      /*
       * Only the library is authored. A personal roleplay came out of
       * somebody's own chat, and nobody edits it — not even an admin, which
       * the API enforces too.
       */
      edit:
        data?.can_manage === true && card.kind === 'library' ? (
          <Link
            to='/practice/$roleplayId/edit'
            params={{ roleplayId: card.id }}
            aria-label={m.practice_edit({ title: card.title })}
            className={iconButtonVariants({
              shape: 'square',
              className: 'bg-fill text-ink-muted hover:bg-line hover:text-ink',
            })}
            {...holdBack(true)}
          >
            <Pencil className='size-3.75' aria-hidden='true' />
          </Link>
        ) : undefined,
      onStart: () => startPractice(card.id),
      onOpenFeedback: () => setFeedbackId(card.id),
    };
  }

  function startPractice(roleplayId: string) {
    setFeedbackId(null);
    start.mutate(roleplayId);
  }

  /**
   * The conversation is done with. It closes, and the page it was held over is
   * where it was left — with the feedback it produced already read inside the
   * popup, which is why the practice was held here and not on a screen of its
   * own.
   *
   * Stable, because a session that was already finished when it opened hands
   * over its feedback the moment it is mounted.
   */
  const closeSession = useCallback(() => {
    setLiveSessionId(null);
    void navigate({ to: '/practice', search: {}, replace: true });
  }, [navigate]);

  /* The link is a way back to this page, not to this dialog. */
  function closeFeedback() {
    setFeedbackId(null);
    if (feedbackRoleplayId !== undefined) {
      void navigate({ to: '/practice', search: {}, replace: true });
    }
  }

  return (
    <>
      <PageTitle>{m.nav_practice()}</PageTitle>
      <PageBody>
        <div className='flex flex-wrap items-center justify-between gap-3'>
          <ViewTabList aria-label={m.practice_tabs()}>
            <ViewTab
              active={tab === 'library'}
              onClick={() => {
                setPickedTab('library');
                setOpenIds([]);
              }}
            >
              {m.practice_tab_library()}
            </ViewTab>
            {!demo && (
              <ViewTab
                active={tab === 'personal'}
                onClick={() => {
                  setPickedTab('personal');
                  setOpenIds([]);
                }}
              >
                {m.practice_tab_personal()}
              </ViewTab>
            )}
          </ViewTabList>

          {data?.can_manage === true && (
            <Link to='/practice/new' className={buttonVariants()} {...holdBack(true)}>
              <Plus className='size-3.5' aria-hidden='true' />
              {m.practice_new()}
            </Link>
          )}
        </div>

        <div className='mt-4.5'>
          {isPending && (
            <p className='text-base text-ink-muted'>{m.practice_loading()}</p>
          )}
          {isError && <p className='text-base text-danger'>{m.practice_failed()}</p>}
          {start.isError && (
            <p role='alert' className='mb-3 text-base text-danger'>
              {m.practice_start_failed()}
            </p>
          )}
          {/* The list snapped back to what the server holds; say why. */}
          {reorder.isError && (
            <p role='alert' className='mb-3 text-base text-danger'>
              {m.practice_move_failed()}
            </p>
          )}

          {empty && tab === 'library' && (
            <PracticeEmpty
              title={m.practice_empty_library_title()}
              body={m.practice_empty_library_body()}
            />
          )}

          {/*
            No button to make one: a personal practice conversation comes out of
            a chat, and pointing at a page that cannot create one would promise
            something that is not there yet.
          */}
          {empty && tab === 'personal' && (
            <PracticeEmpty
              title={m.practice_empty_personal_title()}
              body={m.practice_empty_personal_body()}
            />
          )}

          {cards.length > 0 && (
            <Accordion
              className='gap-3.5'
              value={openIds}
              onValueChange={(value) => setOpenIds(value as string[])}
            >
              {/*
                Only the library has an order somebody decided. The personal
                list is one person's own and is read newest first, so there is
                nothing to arrange there.
              */}
              {sortable ? (
                <PracticeLibraryOrder
                  cards={cards}
                  onReorder={(ids) => reorder.mutate(ids)}
                >
                  {(card, onMove) => (
                    <SortablePracticeCard
                      key={card.id}
                      {...cardProps(card)}
                      onMove={onMove}
                    />
                  )}
                </PracticeLibraryOrder>
              ) : (
                cards.map((card) => <PracticeCard key={card.id} {...cardProps(card)} />)
              )}
            </Accordion>
          )}
        </div>

        {/*
          The one thing people ask before they say anything out loud here.
          Under the list rather than over it: it is a reassurance, not an
          instruction, and nobody should have to read past it to reach the page.
        */}
        <p className='mt-6.5 flex max-w-185 items-start gap-2.5 border-t border-line pt-4 text-base leading-relaxed text-ink-muted'>
          <Lock className='mt-0.75 size-3.75 shrink-0' aria-hidden='true' />
          <span>{m.practice_privacy()}</span>
        </p>
      </PageBody>

      <PracticeFeedbackDialog
        card={feedbackFor}
        starting={start.isPending}
        onClose={closeFeedback}
        onPractiseAgain={() => feedbackFor && startPractice(feedbackFor.id)}
      />

      {liveSessionId !== null && (
        <PracticeSessionModal
          key={liveSessionId}
          sessionId={liveSessionId}
          onLeave={closeSession}
          starting={start.isPending}
          startFailed={start.isError}
          onPractiseAgain={startPractice}
        />
      )}
    </>
  );
}
